import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

const statusPillVariants = cva(
  // Base: T3 .pill recipe (mono, 11px, asymmetric padding for leading dot/icon)
  // — see playbook/design-system/components.md#pill. We re-implement the recipe
  // here rather than wrap `.pill` so the colored dot is part of the same
  // component and tones can be set declaratively.
  [
    'inline-flex items-center gap-2 rounded-full border',
    'py-[5px] pr-[11px] pl-[9px]',
    'mono text-[11px] font-normal',
    'whitespace-nowrap select-none',
  ],
  {
    variants: {
      tone: {
        // Neutral / default — used for "draft", "idle".
        neutral: 'border-line-bright bg-bg-3 text-paper-dim',
        // Active live state — acid pulsing dot. Used for "live", "publishing now".
        live: 'border-line-bright bg-bg-3 text-paper-dim',
        // Pending review — soft amber. Used for "needs approval".
        pending:
          'border-[color:var(--warning)]/30 bg-[color:var(--warning)]/8 text-[color:var(--warning)]',
        // Approved / scheduled — calm acid tint, paper text.
        scheduled: 'bg-acid-tint border-acid/40 text-paper',
        // Published — success green tint.
        published: 'text-success border-[color:var(--success)]/30 bg-[color:var(--success-tint)]',
        // Failed — error red.
        failed:
          'border-[color:var(--alert-error-border)] bg-[color:var(--alert-error-bg)] text-[color:var(--alert-error-text)]',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  }
)

interface StatusPillProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusPillVariants> {
  /** Optional leading dot. `live` tone gets an animated pulsing dot by default. */
  showDot?: boolean
  /** Optional leading icon (lucide). Replaces the dot if both are set. */
  icon?: React.ReactNode
}

/**
 * Semantic status pill for content state — draft, scheduled, published,
 * failed, etc. Mono capsule with an optional leading dot or icon, colored
 * to communicate state without shouting.
 *
 * Spec: playbook/design-system/components.md#status-pill.
 *
 * Examples:
 *
 *   <StatusPill tone="live" showDot>Live</StatusPill>
 *   <StatusPill tone="pending">Needs review</StatusPill>
 *   <StatusPill tone="published" icon={<Check size={10} />}>Published</StatusPill>
 */
export const StatusPill: React.FC<StatusPillProps> = ({
  tone = 'neutral',
  showDot,
  icon,
  className,
  children,
  ...props
}) => {
  // `live` tone implies a pulsing acid dot; everything else has a static
  // colored dot only when the consumer asks for it.
  const wantsDot = showDot ?? tone === 'live'
  const dotIsLive = tone === 'live'

  return (
    <span className={cn(statusPillVariants({ tone }), className)} data-tone={tone} {...props}>
      {icon ? (
        <span aria-hidden className="inline-flex h-2.5 w-2.5 items-center justify-center">
          {icon}
        </span>
      ) : wantsDot ? (
        dotIsLive ? (
          <span className="liveDot" aria-hidden />
        ) : (
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80"
          />
        )
      ) : null}
      {children}
    </span>
  )
}
